<template>
  <div class="modal-card">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <h1 class="title">Delete Ticket</h1>
      <p class="subtitle">This will permanently delete the ticket and all associated data including receivables, payments, and payment allocations. This action cannot be undone.</p>
    </div>
    <div class="modal-card-body">
      <div class="warning-banner">
        This is permanent and cannot be undone. We recommend cancelling the ticket instead, which preserves payment history.
      </div>

      <p class="confirm-prompt">To confirm, type the ticket ID: <strong>{{ ticketId }}</strong></p>
      <div class="form-row">
        <TextInput
          v-model="confirmationInput"
          placeholder="Enter ticket ID..."
        />
      </div>

      <div v-if="error" class="modal-error">
        {{ error }}
      </div>
    </div>
    <div class="modal-card-footer">
      <button class="button form-button close-on-click" @click="handleClose">
        Cancel
      </button>
      <button class="button danger form-button" @click="handleConfirm" :disabled="!isConfirmed || isSubmitting">
        {{ isSubmitting ? 'Deleting...' : 'Delete Ticket' }}
      </button>
    </div>
  </div>
</template>

<script>
import CloseIcon from 'vue-material-design-icons/Close.vue';
import TextInput from '@/components/inputs/TextInput.vue';
import * as api from '@/api';

export default {
  name: "DeleteTicketModal",
  props: {
    ticketId: {
      type: [Number, String],
      required: true
    }
  },
  emits: ['close', 'deleted'],
  data() {
    return {
      confirmationInput: '',
      isSubmitting: false,
      error: null
    }
  },
  computed: {
    isConfirmed() {
      return this.confirmationInput.trim() === String(this.ticketId);
    }
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },
    async handleConfirm() {
      if (!this.isConfirmed) return;

      this.error = null;
      this.isSubmitting = true;

      try {
        await api.deleteTicket(this.ticketId);
        this.$emit('deleted');
      } catch (err) {
        this.error = err.message || 'Failed to delete ticket';
      } finally {
        this.isSubmitting = false;
      }
    }
  },
  components: { CloseIcon, TextInput }
};
</script>

<style scoped>
.modal-card {
  max-width: 450px;
}

.title {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 8px 0 0 0;
}

.modal-card-body {
  margin-top: 20px;
}

.warning-banner {
  padding: 10px 12px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 16px;
}

.confirm-prompt {
  font-size: 14px;
  color: #374151;
  margin: 0 0 12px 0;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.button.danger {
  background-color: #dc2626;
  color: #fff;
  border-color: #dc2626;
  box-shadow: 0 2px 0px #991b1b;
}

.button.danger:hover {
  background-color: #b91c1c;
  border-color: #b91c1c;
}

.button.danger:active {
  background-color: #991b1b;
  border-color: #991b1b;
}

.button.danger:disabled {
  background-color: #f3f4f6;
  color: #9ca3af;
  border-color: #d1d5db;
  box-shadow: none;
}
</style>
